/**
 * Email Signup Block Component
 *
 * Submits the newsletter form to the Klaviyo Client Subscriptions API via AJAX
 * (no page reload) and routes to the language-specific list. Also handles dynamic
 * padding adjustment for integrated buttons.
 */
if (!customElements.get('email-signup-block')) {
  class EmailSignupBlock extends HTMLElement {
    constructor() {
      super();
      /** @type {number} */
      this.lastButtonWidth = 0;
      /** @type {ResizeObserver | undefined} */
      this.observer = undefined;

      this.adjustInputPadding = this.adjustInputPadding.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('.email-signup__button--integrated');
      this.input = this.querySelector('.email-signup__input');
      this.inputGroup = this.querySelector('.email-signup__input-group');
      this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('form.email-signup__form'));

      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
      }

      const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

      if (this.button && this.input && this.inputGroup && isTextButton) {
        this.adjustInputPadding();

        this.observer = new ResizeObserver(() => {
          this.adjustInputPadding();
        });
        this.observer.observe(this.button);

        if ('fonts' in document) {
          document.fonts.ready.then(() => {
            this.adjustInputPadding();
          });
        }
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.form) {
        this.form.removeEventListener('submit', this.handleSubmit);
      }
    }

    /**
     * @param {Event} event
     */
    async handleSubmit(event) {
      event.preventDefault();
      const form = this.form;
      if (!form) return;

      const errorEl = form.querySelector('[data-signup-error]');
      const errorTextEl = form.querySelector('[data-signup-error-text]');
      const fieldsEl = form.querySelector('[data-signup-fields]');
      const successEl = /** @type {HTMLElement | null} */ (form.querySelector('[data-signup-success]'));
      const emailInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="contact[email]"]'));
      const submitButton = /** @type {HTMLButtonElement | null} */ (form.querySelector('.email-signup__button'));
      const errorMessage = form.dataset.errorMessage || 'Something went wrong. Please try again.';

      const showError = () => {
        if (errorTextEl) errorTextEl.textContent = errorMessage;
        errorEl?.classList.remove('hidden');
      };

      errorEl?.classList.add('hidden');

      const publicKey = form.dataset.klaviyoPublicKey;
      const lang = (form.dataset.lang || '').toLowerCase();
      const listId = lang === 'de' && form.dataset.listDe ? form.dataset.listDe : form.dataset.listEn || form.dataset.listDe;
      const email = emailInput?.value.trim();

      if (!publicKey || !listId || !email) {
        showError();
        return;
      }

      if (submitButton) submitButton.disabled = true;

      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
            revision: '2026-04-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Newsletter footer signup',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties: lang ? { language: lang } : {},
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            },
          }),
        });

        if (!response.ok) {
          showError();
          return;
        }

        fieldsEl?.classList.add('hidden');
        if (successEl) {
          successEl.classList.remove('hidden');
          successEl.focus();
        }
      } catch (error) {
        console.error('Klaviyo subscription failed:', error);
        showError();
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    }

    adjustInputPadding() {
      if (!this.button || !this.inputGroup) return;

      const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

      if (buttonWidth !== this.lastButtonWidth) {
        this.lastButtonWidth = buttonWidth;
        /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
      }
    }
  }

  customElements.define('email-signup-block', EmailSignupBlock);
}
